import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  ArrowLeft,
  MapPin,
  Calendar,
  User,
  DollarSign,
  Clock,
  Send,
  Loader2,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Star,
  ExternalLink,
  BarChart3,
  Award,
  CreditCard,
  Lock,
  Flag,
  AlertTriangle,
  MessageSquare,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ rating, setRating, readonly = false }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && setRating(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <Star
            className={`w-6 h-6 transition-colors ${
              star <= (hover || rating)
                ? "fill-[#D4AF37] text-[#D4AF37]"
                : "fill-slate-200 text-slate-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Estimate Comparison Card
const EstimateCompareCard = ({ estimate, isLowest, isCustomer, projectStatus, onStatusUpdate, onViewProfile, onPayNow, paymentStatus, payingEstimateId }) => {
  const [updating, setUpdating] = useState(false);
  const { t } = useTranslation();

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/estimates/${estimate.id}/status`, { status: newStatus });
      toast.success(`Estimate ${newStatus}`);
      onStatusUpdate();
    } catch (error) {
      toast.error(t("project.failedUpdateEstimate"));
    } finally {
      setUpdating(false);
    }
  };

  const canTakeAction = isCustomer && estimate.status === "submitted" && projectStatus === "open";

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div 
      className={`bg-white rounded-xl border-2 p-5 transition-all ${
        estimate.status === "accepted" 
          ? "border-green-400 bg-green-50/30" 
          : isLowest 
            ? "border-[#D4AF37]" 
            : "border-slate-200"
      }`}
      data-testid={`estimate-compare-${estimate.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-[#0B1F3B] flex items-center justify-center text-white font-bold text-lg">
            {estimate.business_name.charAt(0)}
          </div>
          <div>
            <button 
              onClick={() => onViewProfile(estimate.business_id)}
              className="font-semibold text-[#0B1F3B] hover:text-[#D4AF37] flex items-center gap-1"
            >
              {estimate.business_name}
              <ExternalLink className="w-3 h-3" />
            </button>
            <Badge
              className={`text-xs ${
                estimate.status === "accepted"
                  ? "bg-green-100 text-green-700 border-green-200"
                  : estimate.status === "rejected"
                  ? "bg-red-100 text-red-700 border-red-200"
                  : "bg-yellow-100 text-yellow-700 border-yellow-200"
              } border`}
            >
              {estimate.status}
            </Badge>
          </div>
        </div>
        {isLowest && estimate.status === "submitted" && (
          <Badge className="bg-[#D4AF37] text-[#0B1F3B] border-0">
            <Award className="w-3 h-3 mr-1" />
            {t("project.lowest")}
          </Badge>
        )}
      </div>

      {/* Price */}
      <div className="text-center py-4 border-y border-slate-100 mb-4">
        <p className="text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
          {formatCurrency(estimate.amount)}
        </p>
        <p className="text-sm text-slate-500 mt-1">
          <Clock className="w-4 h-4 inline mr-1" />
          {estimate.timeline}
        </p>
      </div>

      {/* Description */}
      <p className="text-sm text-slate-600 mb-4 line-clamp-3">{estimate.description}</p>

      {/* Actions */}
      {canTakeAction && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => handleStatusUpdate("accepted")}
            disabled={updating}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            data-testid={`accept-estimate-${estimate.id}`}
          >
            {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            {t("project.accept")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleStatusUpdate("rejected")}
            disabled={updating}
            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-estimate-${estimate.id}`}
          >
            <XCircle className="w-4 h-4 mr-1" />
            {t("project.reject")}
          </Button>
        </div>
      )}

      {estimate.status === "accepted" && (
        <div className="flex items-center justify-center gap-2 text-green-600 py-2">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">{t("project.selectedContractor")}</span>
        </div>
      )}
    </div>
  );
};

// Project Images Component
const ProjectImages = ({ images, currentIndex, setCurrentIndex }) => {
  if (!images || images.length === 0) return null;
  
  const totalImages = images.length;
  const currentImage = images[currentIndex];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
        <h3 className="font-semibold text-[#0B1F3B]">Project Photos</h3>
      </div>
      <div className="relative">
        <img
          src={currentImage}
          alt={`Project image ${currentIndex + 1}`}
          className="w-full h-64 md:h-96 object-cover rounded-lg"
        />
        {totalImages > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex(currentIndex === 0 ? totalImages - 1 : currentIndex - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentIndex(currentIndex === totalImages - 1 ? 0 : currentIndex + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((img, idx) => (
                <button
                  key={`dot-${img}-${idx}`}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full ${idx === currentIndex ? "bg-white" : "bg-white/50"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const [project, setProject] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [existingReview, setExistingReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [estimateForm, setEstimateForm] = useState({
    amount: "",
    description: "",
    timeline: "",
  });

  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    comment: "",
  });

  const [payingEstimateId, setPayingEstimateId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [milestoneLoading, setMilestoneLoading] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [showDisputeForm, setShowDisputeForm] = useState(null); // milestone_id
  const [disputeForm, setDisputeForm] = useState({ reason: "", description: "", evidence_urls: [] });
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);

  const isCustomer = user?.user_type === "customer";
  const isBusiness = user?.user_type === "business";

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 6;
    if (attempt >= maxAttempts) {
      toast.error(t("project.paymentTimeout"));
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPaymentStatus("paid");
        toast.success(t("project.paymentSuccess"));
        fetchProject();
        return;
      }
      if (res.data.status === "expired") {
        toast.error(t("project.paymentExpired"));
        return;
      }
      // Still pending — poll again
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
    } catch {
      toast.error(t("project.failedPayment"));
    }
  };

  const handlePayNow = async (estimateId) => {
    setPayingEstimateId(estimateId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(
        `${API}/payments/create?estimate_id=${estimateId}`,
        {},
        { headers: { Origin: origin } }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create payment";
      toast.error(msg);
    } finally {
      setPayingEstimateId(null);
    }
  };

  const handlePayMilestone = async (milestoneId) => {
    setMilestoneLoading(milestoneId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/milestones/${milestoneId}/pay`, {}, {
        headers: { Origin: origin }
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start payment");
    } finally {
      setMilestoneLoading(null);
    }
  };

  const handleReleaseMilestone = async (milestoneId) => {
    setReleaseLoading(milestoneId);
    try {
      const res = await axios.post(`${API}/milestones/${milestoneId}/release`);
      toast.success(`Funds released! Contractor receives $${res.data.contractor_amount.toFixed(2)}`);
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to release funds");
    } finally {
      setReleaseLoading(null);
    }
  };

  const handleDisputeSubmit = async (e) => {
    e.preventDefault();
    if (!disputeForm.reason) { toast.error(t("project.selectReason")); return; }
    setDisputeSubmitting(true);
    try {
      await axios.post(`${API}/disputes/create`, {
        project_id: projectId,
        milestone_id: showDisputeForm,
        reason: disputeForm.reason,
        description: disputeForm.description,
        evidence_urls: disputeForm.evidence_urls,
      });
      toast.success(t("project.disputeOpened"));
      setShowDisputeForm(null);
      setDisputeForm({ reason: "", description: "", evidence_urls: [] });
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open dispute");
    } finally {
      setDisputeSubmitting(false);
    }
  };

  const handleDisputeEvidenceUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/verification`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setDisputeForm(prev => ({ ...prev, evidence_urls: [...prev.evidence_urls, res.data.url] }));
      toast.success("Evidence uploaded");
    } catch {
      toast.error("Failed to upload evidence");
    }
  };

  const fetchProject = async () => {
    try {
      const [projectRes, estimatesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/estimates/project/${projectId}`),
      ]);
      setProject(projectRes.data);
      setEstimates(estimatesRes.data);

      // Fetch milestones if any estimate is accepted
      const hasAccepted = estimatesRes.data.some(e => e.status === "accepted");
      if (hasAccepted) {
        try {
          const msRes = await axios.get(`${API}/milestones/project/${projectId}`);
          setMilestones(msRes.data);
        } catch { /* no milestones yet */ }
        try {
          const dRes = await axios.get(`${API}/disputes/project/${projectId}`);
          setDisputes(dRes.data);
        } catch { /* no disputes */ }
      }

      // Check for existing review if project is completed
      if (projectRes.data.status === "completed" && isCustomer) {
        try {
          const reviewRes = await axios.get(`${API}/reviews/project/${projectId}`);
          setExistingReview(reviewRes.data);
        } catch (e) {
          // No review yet
        }
      }
    } catch (error) {
      toast.error("Failed to load project");
      navigate(isCustomer ? "/customer" : "/business");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEstimateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitEstimate = async (e) => {
    e.preventDefault();

    if (!estimateForm.amount || !estimateForm.description || !estimateForm.timeline) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, {
        project_id: projectId,
        amount: parseFloat(estimateForm.amount),
        description: estimateForm.description,
        timeline: estimateForm.timeline,
      });
      toast.success(t("project.estimateSubmitted"));
      setDialogOpen(false);
      setEstimateForm({ amount: "", description: "", timeline: "" });
      fetchProject();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit estimate";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteProject = async () => {
    setCompleting(true);
    try {
      const response = await axios.put(`${API}/projects/${projectId}/complete`);
      toast.success(t("project.projectCompleted"));
      
      // Open review dialog
      setReviewForm({
        ...reviewForm,
        businessId: response.data.business_id,
      });
      setReviewDialogOpen(true);
      fetchProject();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to complete project";
      toast.error(message);
    } finally {
      setCompleting(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (reviewForm.rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (!reviewForm.comment.trim()) {
      toast.error("Please write a review");
      return;
    }

    const acceptedEstimate = estimates.find(e => e.status === "accepted");
    if (!acceptedEstimate) {
      toast.error("No accepted estimate found");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        project_id: projectId,
        business_id: acceptedEstimate.business_id,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });
      toast.success("Review submitted! Thank you for your feedback.");
      setReviewDialogOpen(false);
      fetchProject();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit review";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewBusinessProfile = (businessId) => {
    navigate(`/business/view/${businessId}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const hasSubmittedEstimate = estimates.some((e) => e.business_id === user?.id);
  const acceptedEstimate = estimates.find((e) => e.status === "accepted");
  const lowestEstimate = estimates.length > 0 
    ? estimates.reduce((min, e) => e.amount < min.amount ? e : min, estimates[0])
    : null;

  const getStatusInfo = (status) => {
    const config = {
      open: { label: "Open", style: "bg-green-100 text-green-700 border-green-200" },
      in_progress: { label: "In Progress", style: "bg-yellow-100 text-yellow-700 border-yellow-200" },
      paid: { label: "Paid", style: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      completed: { label: "Completed", style: "bg-blue-100 text-blue-700 border-blue-200" },
      cancelled: { label: "Cancelled", style: "bg-red-100 text-red-700 border-red-200" },
    };
    return config[status] || config.open;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!project) return null;

  const statusInfo = getStatusInfo(project.status);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={isCustomer ? "/customer" : "/business"}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="font-bold text-[#0B1F3B]">MyProjectBidder</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Complete Project Button */}
              {isCustomer && (project.status === "in_progress" || project.status === "paid") && (
                <Button
                  onClick={handleCompleteProject}
                  disabled={completing}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold"
                  data-testid="complete-project-btn"
                >
                  {completing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Mark Complete
                </Button>
              )}

              {/* Leave Review Button */}
              {isCustomer && project.status === "completed" && !existingReview && acceptedEstimate && (
                <Button
                  onClick={() => setReviewDialogOpen(true)}
                  className="gold-gradient text-[#0B1F3B] hover:brightness-110 font-bold"
                  data-testid="leave-review-btn"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Leave Review
                </Button>
              )}

              {/* Submit Estimate Button for Business */}
              {isBusiness && !hasSubmittedEstimate && project.status === "open" && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="gold-gradient text-[#0B1F3B] hover:brightness-110 font-bold"
                      data-testid="submit-estimate-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Submit Estimate
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-[#0B1F3B]">Submit Your Estimate</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitEstimate} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="amount" className="text-[#0B1F3B] font-medium">
                          Estimated Price ($) *
                        </Label>
                        <Input
                          id="amount"
                          name="amount"
                          type="number"
                          step="0.01"
                          min="0"
                          value={estimateForm.amount}
                          onChange={handleInputChange}
                          placeholder="e.g., 5000"
                          className="mt-1.5 h-12"
                          data-testid="estimate-amount-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="timeline" className="text-[#0B1F3B] font-medium">
                          Estimated Timeline *
                        </Label>
                        <Input
                          id="timeline"
                          name="timeline"
                          value={estimateForm.timeline}
                          onChange={handleInputChange}
                          placeholder="e.g., 2-3 weeks"
                          className="mt-1.5 h-12"
                          data-testid="estimate-timeline-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="description" className="text-[#0B1F3B] font-medium">
                          Description *
                        </Label>
                        <Textarea
                          id="description"
                          name="description"
                          value={estimateForm.description}
                          onChange={handleInputChange}
                          placeholder="Describe what's included in your estimate..."
                          className="mt-1.5 min-h-[100px]"
                          data-testid="estimate-description-input"
                        />
                      </div>
                      <div className="flex gap-3 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDialogOpen(false)}
                          className="flex-1"
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button
                          type="submit"
                          disabled={submitting}
                          className="flex-1 bg-[#0B1F3B] hover:bg-[#162A4C] text-white font-bold"
                          data-testid="confirm-estimate-btn"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {t("project.submittingEstimate")}
                            </>
                          ) : (
                            t("project.submitEstimateBtn")
                          )}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B1F3B]">Leave a Review</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitReview} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#0B1F3B] font-medium mb-3 block">
                How was your experience?
              </Label>
              <StarRating rating={reviewForm.rating} setRating={(r) => setReviewForm({...reviewForm, rating: r})} />
            </div>
            <div>
              <Label htmlFor="comment" className="text-[#0B1F3B] font-medium">
                Your Review
              </Label>
              <Textarea
                id="comment"
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                placeholder="Share your experience with this contractor..."
                className="mt-1.5 min-h-[120px]"
                data-testid="review-comment-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setReviewDialogOpen(false)}
                className="flex-1"
              >
                Maybe Later
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 gold-gradient text-[#0B1F3B] hover:brightness-110 font-bold"
                data-testid="submit-review-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Star className="w-4 h-4 mr-2" />
                )}
                Submit Review
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8" data-testid="project-detail">
        {/* Project Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
                  {project.title}
                </h1>
                <Badge className={`${statusInfo.style} border`}>
                  {statusInfo.label}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {project.category_name}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {project.city}, {project.zip_code}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(project.created_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h3 className="font-semibold text-[#0B1F3B] mb-2">Project Description</h3>
            <p className="text-slate-600 whitespace-pre-wrap">{project.description}</p>
          </div>

          {isCustomer && (
            <div className="border-t border-slate-100 pt-4 mt-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Posted by you</span>
              </div>
            </div>
          )}

          {isBusiness && (
            <div className="border-t border-slate-100 pt-4 mt-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Customer: {project.customer_name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Project Images */}
        <ProjectImages 
          images={project.images || []} 
          currentIndex={currentImageIndex}
          setCurrentIndex={setCurrentImageIndex}
        />

        {/* Existing Review Display */}
        {existingReview && (
          <div className="bg-green-50 rounded-xl border border-green-200 p-6 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-green-800">Your Review</h3>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <StarRating rating={existingReview.rating} readonly />
            </div>
            <p className="text-slate-600">{existingReview.comment}</p>
          </div>
        )}

        {/* Estimates Section */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#0B1F3B]">
                {isCustomer ? "Received Estimates" : "Estimates"} ({estimates.length})
              </h2>
              {estimates.length > 0 && isCustomer && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <BarChart3 className="w-4 h-4" />
                  <span>Compare estimates below</span>
                </div>
              )}
            </div>
          </div>

          {estimates.length === 0 ? (
            <div className="p-12 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-[#0B1F3B] mb-2">No estimates yet</h3>
              <p className="text-slate-500">
                {isCustomer
                  ? "Contractors will submit their estimates soon."
                  : "Be the first to submit an estimate for this project!"}
              </p>
            </div>
          ) : (
            <div className="p-6">
              {/* Comparison Grid for Customers */}
              {isCustomer && estimates.length >= 1 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {estimates.map((estimate) => (
                    <EstimateCompareCard
                      key={estimate.id}
                      estimate={estimate}
                      isLowest={lowestEstimate?.id === estimate.id && estimate.status === "submitted"}
                      isCustomer={isCustomer}
                      projectStatus={project.status}
                      onStatusUpdate={fetchProject}
                      onViewProfile={handleViewBusinessProfile}
                      onPayNow={handlePayNow}
                      paymentStatus={paymentStatus}
                      payingEstimateId={payingEstimateId}
                    />
                  ))}
                </div>
              ) : (
                // List view for businesses
                <div className="space-y-4">
                  {estimates.map((estimate) => (
                    <div
                      key={estimate.id}
                      className="p-4 border border-slate-200 rounded-lg"
                      data-testid={`estimate-${estimate.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#0B1F3B] flex items-center justify-center text-white font-bold">
                            {estimate.business_name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-[#0B1F3B]">{estimate.business_name}</p>
                            <p className="text-sm text-slate-500">{formatDate(estimate.created_at)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#0B1F3B]">{formatCurrency(estimate.amount)}</p>
                          <Badge
                            className={`${
                              estimate.status === "accepted"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : estimate.status === "rejected"
                                ? "bg-red-100 text-red-700 border-red-200"
                                : "bg-yellow-100 text-yellow-700 border-yellow-200"
                            } border`}
                          >
                            {estimate.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Milestone Payment Section — Homeowner Only */}
        {isCustomer && milestones.length > 0 && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="milestones-section">
            <div className="p-5 border-b border-slate-200 bg-slate-50">
              <h3 className="text-lg font-bold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>Payment Milestones</h3>
              <p className="text-sm text-slate-500 mt-1">Funds are held in escrow and released to the contractor when you approve each milestone.</p>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {milestones.map((ms) => {
                const platformFee = Math.round(ms.amount * 0.10 * 100) / 100;
                const contractorAmt = Math.round((ms.amount - platformFee) * 100) / 100;
                const prevMilestone = milestones.find(m => m.order === ms.order - 1);
                const prevPaid = !prevMilestone || prevMilestone.status !== "pending";
                const isLocked = ms.order > 1 && !prevPaid;
                const msDispute = disputes.find(d => d.milestone_id === ms.id && ["open", "under_review"].includes(d.status));
                const msResolved = disputes.find(d => d.milestone_id === ms.id && d.status.startsWith("resolved_"));
                const hasOpenDispute = !!msDispute;

                return (
                  <div
                    key={ms.id}
                    className={`rounded-xl border-2 p-5 transition-all ${
                      hasOpenDispute ? "border-red-300 bg-red-50/30"
                      : ms.status === "released" ? "border-emerald-300 bg-emerald-50/30"
                      : ms.status === "payment_held" ? "border-[#D4AF37] bg-amber-50/30"
                      : ms.status === "cancelled" ? "border-slate-300 bg-slate-50 opacity-60"
                      : isLocked ? "border-slate-200 bg-slate-50 opacity-60"
                      : "border-slate-200"
                    }`}
                    data-testid={`milestone-card-${ms.order}`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          hasOpenDispute ? "bg-red-100 text-red-700"
                          : ms.status === "released" ? "bg-emerald-100 text-emerald-700"
                          : ms.status === "payment_held" ? "bg-[#D4AF37] text-[#0B1F3B]"
                          : "bg-slate-200 text-slate-600"
                        }`}>
                          {ms.order}
                        </div>
                        <div>
                          <p className="font-semibold text-[#0B1F3B] text-sm">{ms.title}</p>
                          <p className="text-xs text-slate-500">{ms.percentage}% of total</p>
                        </div>
                      </div>
                      <Badge className={`text-xs border ${
                        hasOpenDispute ? "bg-red-100 text-red-700 border-red-200"
                        : ms.status === "released" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : ms.status === "cancelled" ? "bg-slate-100 text-slate-600 border-slate-200"
                        : ms.status === "payment_held" ? "bg-amber-100 text-amber-700 border-amber-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                      }`} data-testid={`milestone-status-${ms.order}`}>
                        {hasOpenDispute ? "Disputed" : ms.status === "payment_held" ? "Held in Escrow" : ms.status === "released" ? "Released" : ms.status === "cancelled" ? "Cancelled" : isLocked ? "Locked" : "Pending"}
                      </Badge>
                    </div>

                    {/* Amount */}
                    <div className="text-center py-3 border-y border-slate-100 mb-3">
                      <p className="text-2xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
                        ${ms.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </p>
                    </div>

                    {/* Fee Breakdown */}
                    <div className="space-y-1 mb-4 text-xs text-slate-500">
                      <div className="flex justify-between">
                        <span>Platform fee (10%)</span>
                        <span className="text-slate-700 font-medium">${platformFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Contractor receives</span>
                        <span className="text-slate-700 font-medium">${contractorAmt.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Dispute Banner */}
                    {hasOpenDispute && (
                      <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid={`dispute-banner-${ms.order}`}>
                        <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-1">
                          <Flag className="w-4 h-4" /> Dispute Under Review
                        </div>
                        <p className="text-xs text-red-600">Funds are frozen until admin resolves this dispute.</p>
                      </div>
                    )}
                    {msResolved && (
                      <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                        Dispute resolved: {msResolved.status.replace("resolved_", "").replace("_", " ")}
                      </div>
                    )}

                    {/* Actions */}
                    {ms.status === "pending" && !isLocked && (
                      <Button
                        onClick={() => handlePayMilestone(ms.id)}
                        disabled={milestoneLoading === ms.id}
                        className="w-full bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 text-white font-bold"
                        data-testid={`pay-milestone-${ms.order}`}
                      >
                        {milestoneLoading === ms.id
                          ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          : <CreditCard className="w-4 h-4 mr-2" />}
                        Pay Deposit
                      </Button>
                    )}
                    {ms.status === "pending" && isLocked && (
                      <div className="flex items-center justify-center gap-2 text-slate-400 py-2">
                        <Lock className="w-4 h-4" />
                        <span className="text-sm font-medium">Pay Milestone 1 first</span>
                      </div>
                    )}
                    {ms.status === "payment_held" && !hasOpenDispute && ms.order === 1 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2 text-amber-600 py-2 bg-amber-50 rounded-lg">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">Awaiting work completion</span>
                        </div>
                        {(isCustomer || isBusiness) && (
                          <Button variant="outline" size="sm" onClick={() => { setShowDisputeForm(ms.id); setDisputeForm({ reason: "", description: "", evidence_urls: [] }); }}
                            className="w-full border-red-200 text-red-600 hover:bg-red-50 text-xs" data-testid={`open-dispute-${ms.order}`}>
                            <Flag className="w-3 h-3 mr-1" /> Open Dispute
                          </Button>
                        )}
                      </div>
                    )}
                    {ms.status === "payment_held" && !hasOpenDispute && ms.order > 1 && (
                      <div className="space-y-2">
                        <Button
                          onClick={() => handleReleaseMilestone(ms.id)}
                          disabled={releaseLoading === ms.id}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                          data-testid={`release-milestone-${ms.order}`}
                        >
                          {releaseLoading === ms.id
                            ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            : <CheckCircle2 className="w-4 h-4 mr-2" />}
                          Approve & Release Payment
                        </Button>
                        {(isCustomer || isBusiness) && (
                          <Button variant="outline" size="sm" onClick={() => { setShowDisputeForm(ms.id); setDisputeForm({ reason: "", description: "", evidence_urls: [] }); }}
                            className="w-full border-red-200 text-red-600 hover:bg-red-50 text-xs" data-testid={`open-dispute-${ms.order}`}>
                            <Flag className="w-3 h-3 mr-1" /> Open Dispute
                          </Button>
                        )}
                      </div>
                    )}
                    {ms.status === "payment_held" && hasOpenDispute && (
                      <div className="flex items-center justify-center gap-2 text-red-500 py-2">
                        <Lock className="w-4 h-4" />
                        <span className="text-sm font-medium">Funds frozen — dispute in progress</span>
                      </div>
                    )}
                    {ms.status === "released" && (
                      <div className="flex items-center justify-center gap-2 text-emerald-600 py-2 bg-emerald-50 rounded-lg" data-testid={`milestone-released-${ms.order}`}>
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-sm font-medium">Released to contractor</span>
                      </div>
                    )}
                    {ms.status === "cancelled" && (
                      <div className="flex items-center justify-center gap-2 text-slate-500 py-2">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">Cancelled</span>
                      </div>
                    )}

                    {/* Inline Dispute Form */}
                    {showDisputeForm === ms.id && (
                      <form onSubmit={handleDisputeSubmit} className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg space-y-3" data-testid={`dispute-form-${ms.order}`}>
                        <p className="text-sm font-semibold text-red-800">Open a Dispute</p>
                        <select
                          value={disputeForm.reason}
                          onChange={(e) => setDisputeForm(p => ({ ...p, reason: e.target.value }))}
                          className="w-full h-10 rounded-md border border-red-200 bg-white px-3 text-sm"
                          data-testid="dispute-reason"
                        >
                          <option value="">Select reason...</option>
                          <option value="quality">Quality of work</option>
                          <option value="timeline">Timeline not met</option>
                          <option value="scope">Scope disagreement</option>
                          <option value="payment">Payment issue</option>
                          <option value="communication">Communication breakdown</option>
                          <option value="other">Other</option>
                        </select>
                        <textarea
                          value={disputeForm.description}
                          onChange={(e) => setDisputeForm(p => ({ ...p, description: e.target.value }))}
                          placeholder="Describe the issue in detail..."
                          className="w-full min-h-[80px] rounded-md border border-red-200 bg-white px-3 py-2 text-sm"
                          data-testid="dispute-description"
                        />
                        <div>
                          <label className="flex items-center gap-2 text-xs text-red-700 cursor-pointer">
                            <Upload className="w-4 h-4" /> Upload evidence (optional)
                            <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => { if (e.target.files[0]) handleDisputeEvidenceUpload(e.target.files[0]); }} />
                          </label>
                          {disputeForm.evidence_urls.length > 0 && (
                            <div className="mt-1 space-y-1">
                              {disputeForm.evidence_urls.map((url, i) => (
                                <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 hover:underline truncate">Evidence {i + 1}</a>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={() => setShowDisputeForm(null)} className="flex-1">Cancel</Button>
                          <Button type="submit" size="sm" disabled={disputeSubmitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="submit-dispute">
                            {disputeSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Flag className="w-4 h-4 mr-1" />}
                            Submit Dispute
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Business already submitted banner */}
        {isBusiness && hasSubmittedEstimate && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Send className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-green-800">
              You've already submitted an estimate for this project.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
